/**
 * The pieces every part of the table is built from: wooden tokens, leaders and
 * discs in seat colours, painted action markers, bevelled cardboard tiles,
 * cards, and the shadows and glows under them. Each returns a Container placed
 * at the origin, so callers position it and nothing here knows about layout.
 */
import type { Color } from '@trajan/engine';
import {
  Container,
  FillGradient,
  Graphics,
  NineSliceSprite,
  Sprite,
  Text,
  type TextStyleOptions,
} from 'pixi.js';
import { DISPLAY_FACE, TEXT_FACE } from '../fonts';
import {
  SHADOW_SLICE,
  cardBackTexture,
  dotShadowTexture,
  grainTexture,
  shadowTexture,
} from '../materials';
import { CARD, INK, MARKER_PAINT, mix, seatPaint, type Paint } from '../palette';

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

/** How finely text is rasterised: the table's scale times the screen's pixel ratio. */
let textResolution = 2;
export const setTextResolution = (r: number): void => {
  textResolution = Math.min(4, Math.max(1, r));
};

export type Face = 'display' | 'text';

/** Text on the table, in one of its two faces, centred on its anchor by default. */
export function label(
  content: string,
  size: number,
  opts: {
    face?: Face;
    weight?: '400' | '500' | '600' | '700';
    fill?: number;
    anchor?: [number, number];
    spacing?: number;
    halo?: number;
    wrap?: number;
    align?: 'left' | 'center' | 'right';
  } = {},
): Text {
  const style: TextStyleOptions = {
    fontFamily: opts.face === 'display' ? DISPLAY_FACE : TEXT_FACE,
    fontSize: size,
    fontWeight: opts.weight ?? (opts.face === 'display' ? '700' : '500'),
    fill: opts.fill ?? INK,
    letterSpacing: opts.spacing ?? 0,
    align: opts.align ?? 'center',
    ...(opts.halo !== undefined
      ? { stroke: { color: opts.halo, width: Math.max(2, size / 5), join: 'round' as const } }
      : {}),
    ...(opts.wrap !== undefined ? { wordWrap: true, wordWrapWidth: opts.wrap } : {}),
  };
  const t = new Text({ text: content, style, resolution: textResolution });
  const [ax, ay] = opts.anchor ?? [0.5, 0.5];
  t.anchor.set(ax, ay);
  return t;
}

/** Engraved lettering: dark ink with a pale line under it, as if cut into the stone. */
export function engraved(content: string, size: number, fill: number, light = 0xfff8ea): Container {
  const c = new Container();
  const under = label(content, size, { face: 'display', fill: light, spacing: size * 0.12 });
  under.alpha = 0.7;
  under.position.set(0, 1);
  c.addChild(under, label(content, size, { face: 'display', fill, spacing: size * 0.12 }));
  return c;
}

// ---------------------------------------------------------------------------
// Shadows and glows
// ---------------------------------------------------------------------------

/** A soft shadow under a `w` × `h` rectangle, dropped by `drop` towards the viewer. */
export function boxShadow(
  w: number,
  h: number,
  drop = 3,
  alpha = 0.42,
  spread = 4,
): NineSliceSprite {
  const s = new NineSliceSprite({
    texture: shadowTexture(),
    leftWidth: SHADOW_SLICE,
    rightWidth: SHADOW_SLICE,
    topHeight: SHADOW_SLICE,
    bottomHeight: SHADOW_SLICE,
  });
  const pad = 30 - 8 + spread;
  s.width = w + 2 * pad;
  s.height = h + 2 * pad;
  s.position.set(-pad + drop * 0.4, -pad + drop);
  s.alpha = alpha;
  return s;
}

/** A round shadow under a piece of radius `r`. */
export function dotShadow(r: number, drop = 2, alpha = 0.55): Sprite {
  const s = new Sprite(dotShadowTexture());
  s.anchor.set(0.5);
  s.width = r * 2.9;
  s.height = r * 2.5;
  s.position.set(drop * 0.4, drop + r * 0.15);
  s.alpha = alpha;
  return s;
}

// ---------------------------------------------------------------------------
// Wooden pieces
// ---------------------------------------------------------------------------

const gradients = new Map<string, FillGradient>();
/** A radial gradient lit from the upper left, as wood under a lamp. */
function lit(p: Paint): FillGradient {
  const key = `${p.base}`;
  let g = gradients.get(key);
  if (!g) {
    g = new FillGradient({
      type: 'radial',
      center: { x: 0.36, y: 0.3 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.62,
      colorStops: [
        { offset: 0, color: p.light },
        { offset: 0.55, color: p.base },
        { offset: 1, color: p.dark },
      ],
      textureSpace: 'local',
    });
    gradients.set(key, g);
  }
  return g;
}

/** A seat's wooden token — legionnaire, worker — seen from above. */
export function seatToken(seat: number, r = 6): Container {
  const p = seatPaint(seat);
  const c = new Container();
  c.addChild(dotShadow(r));
  const g = new Graphics();
  g.circle(0, 0.8, r).fill(p.dark);
  g.circle(0, 0, r).fill(lit(p)).stroke({ width: 0.8, color: p.dark, alpha: 0.9 });
  c.addChild(g);
  return c;
}

/** A seat's leader: a taller pawn with a crest, so it never reads as a token. */
export function leaderPawn(seat: number, size = 16): Container {
  const p = seatPaint(seat);
  const c = new Container();
  c.addChild(dotShadow(size * 0.36, 2, 0.6));
  const g = new Graphics();
  const s = size / 16;
  g.roundRect(-5.5 * s, -2 * s, 11 * s, 9.5 * s, 2.5 * s).fill(lit(p));
  g.circle(0, -5 * s, 4.2 * s).fill(lit(p));
  g.moveTo(-3.5 * s, -8.2 * s).quadraticCurveTo(0, -13 * s, 3.5 * s, -8.2 * s);
  g.stroke({ width: 2.2 * s, color: p.dark, cap: 'round' });
  g.roundRect(-5.5 * s, -2 * s, 11 * s, 9.5 * s, 2.5 * s).stroke({ width: 0.8, color: p.dark });
  g.circle(0, -5 * s, 4.2 * s).stroke({ width: 0.8, color: p.dark });
  c.addChild(g);
  return c;
}

/** A senate disc: a flat token seen at a slant, so a stack reads as a stack. */
export function senateDisc(seat: number, w = 24): Container {
  const p = seatPaint(seat);
  const c = new Container();
  const g = new Graphics();
  const h = w * 0.42;
  g.ellipse(0, 3.5, w / 2, h / 2).fill(p.dark);
  g.rect(-w / 2, 0, w, 3.5).fill(p.dark);
  g.ellipse(0, 0, w / 2, h / 2)
    .fill(lit(p))
    .stroke({ width: 0.8, color: p.dark });
  c.addChild(g);
  return c;
}

// ---------------------------------------------------------------------------
// Action markers: a colour and a glyph each, so the six never depend on hue
// ---------------------------------------------------------------------------

function markerGlyph(g: Graphics, color: Color, r: number, ink: number) {
  switch (color) {
    case 'yellow':
      g.circle(0, 0, r * 0.42).fill(ink);
      return;
    case 'orange':
      g.poly([0, -r * 0.52, r * 0.5, r * 0.38, -r * 0.5, r * 0.38]).fill(ink);
      return;
    case 'green':
      g.rect(-r * 0.38, -r * 0.38, r * 0.76, r * 0.76).fill(ink);
      return;
    case 'white':
      g.poly([0, -r * 0.55, r * 0.5, 0, 0, r * 0.55, -r * 0.5, 0]).fill(ink);
      return;
    case 'pink':
      g.star(0, 0, 5, r * 0.56, r * 0.24).fill(ink);
      return;
    case 'blue': {
      const a = r * 0.16;
      const b = r * 0.52;
      g.poly([
        -a,
        -b,
        a,
        -b,
        a,
        -a,
        b,
        -a,
        b,
        a,
        a,
        a,
        a,
        b,
        -a,
        b,
        -a,
        a,
        -b,
        a,
        -b,
        -a,
        -a,
        -a,
      ]).fill(ink);
      return;
    }
  }
}

/** A painted wooden action marker, `size` across, centred on the origin. */
export function actionMarker(color: Color, size = 15, shadow = true): Container {
  const p = MARKER_PAINT[color];
  const r = size / 2;
  const c = new Container();
  if (shadow) c.addChild(dotShadow(r * 0.95, 1.6, 0.5));
  const g = new Graphics();
  g.regularPoly(0, 1, r, 8, Math.PI / 8).fill(p.dark);
  g.regularPoly(0, 0, r, 8, Math.PI / 8)
    .fill(lit(p))
    .stroke({ width: 0.8, color: p.dark });
  markerGlyph(g, color, r, mix(p.dark, 0x000000, 0.35));
  c.addChild(g);
  return c;
}

// ---------------------------------------------------------------------------
// Tiles and cards
// ---------------------------------------------------------------------------

const tileGradients = new Map<string, FillGradient>();
function face(p: Paint): FillGradient {
  const key = `${p.base}`;
  let g = tileGradients.get(key);
  if (!g) {
    g = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0.35, y: 1 },
      colorStops: [
        { offset: 0, color: p.light },
        { offset: 0.45, color: p.base },
        { offset: 1, color: mix(p.base, p.dark, 0.35) },
      ],
      textureSpace: 'local',
    });
    tileGradients.set(key, g);
  }
  return g;
}

/**
 * A cardboard tile `w` × `h` with its top-left at the origin: a thickness along
 * the bottom edge, a lit face, a pale bevel and a paper grain.
 */
export function tile(
  w: number,
  h: number,
  p: Paint,
  opts: { radius?: number; shadow?: boolean; lift?: number } = {},
): Container {
  const r = opts.radius ?? Math.min(7, w * 0.12);
  const c = new Container();
  if (opts.shadow !== false) c.addChild(boxShadow(w, h, 3 + (opts.lift ?? 0)));
  const g = new Graphics();
  g.roundRect(0, 2, w, h, r).fill(p.dark);
  g.roundRect(0, 0, w, h, r).fill(face(p));
  g.roundRect(1, 1, w - 2, h - 2, r - 1).stroke({ width: 1.2, color: 0xffffff, alpha: 0.38 });
  g.roundRect(0, 0, w, h, r).stroke({ width: 0.8, color: p.dark, alpha: 0.8 });
  c.addChild(g);
  const grain = new Graphics()
    .roundRect(0, 0, w, h, r)
    .fill({ texture: grainTexture(), alpha: 0.9 });
  c.addChild(grain);
  return c;
}

/** An empty space a tile could stand on: a faint carved outline. */
export function emptySpace(w: number, h: number, radius = 7): Graphics {
  const g = new Graphics();
  g.roundRect(0, 0, w, h, radius).fill({ color: 0x000000, alpha: 0.08 });
  g.roundRect(0.5, 1.5, w - 1, h - 1.5, radius).stroke({ width: 1, color: 0xffffff, alpha: 0.35 });
  g.roundRect(0, 0, w, h, radius).stroke({ width: 1, color: 0x000000, alpha: 0.16 });
  return g;
}

/** A card, face up, with its top-left at the origin. */
export function cardFace(w: number, h: number, shadow = true): Container {
  const c = new Container();
  if (shadow) c.addChild(boxShadow(w, h, 2.5, 0.4, 2));
  const g = new Graphics();
  g.roundRect(0, 0, w, h, 5).fill(CARD.face).stroke({ width: 1, color: CARD.edge });
  g.roundRect(3, 3, w - 6, h - 6, 3).stroke({ width: 0.8, color: CARD.edge, alpha: 0.8 });
  c.addChild(g);
  c.addChild(new Graphics().roundRect(0, 0, w, h, 5).fill({ texture: grainTexture(), alpha: 0.7 }));
  return c;
}

/**
 * The back of a card, the same for every card: the generated gold-on-red back
 * stretched over the card, or, if its image did not load, a deep red with a
 * bronze lattice drawn here.
 */
export function cardBack(w: number, h: number, shadow = true): Container {
  const c = new Container();
  if (shadow) c.addChild(boxShadow(w, h, 2.5, 0.4, 2));
  const g = new Graphics();
  const art = cardBackTexture();
  if (art) {
    g.roundRect(0, 0, w, h, 5)
      .fill({ texture: art, textureSpace: 'local' })
      .stroke({ width: 1, color: 0x3d1510 });
    c.addChild(g);
    return c;
  }
  g.roundRect(0, 0, w, h, 5).fill(0x6e2a22).stroke({ width: 1, color: 0x3d1510 });
  g.roundRect(4, 4, w - 8, h - 8, 3).stroke({ width: 1, color: 0xd9a55a, alpha: 0.8 });
  for (let k = 1; k < 4; k++) {
    g.moveTo(4, 4 + ((h - 8) * k) / 4).lineTo(w - 4, 4 + ((h - 8) * k) / 4);
  }
  g.stroke({ width: 0.6, color: 0xd9a55a, alpha: 0.35 });
  g.circle(w / 2, h / 2, Math.min(w, h) * 0.18).stroke({ width: 1.2, color: 0xd9a55a, alpha: 0.9 });
  c.addChild(g);
  return c;
}
